/**
 * Which front end a browser should get.
 *
 * The mobile app plays the game — camera, Spotify Connect, one thumb. The
 * desktop app authors playlists — a wide table, a keyboard, many tracks at
 * once. Both are served from the same URL by one host app, which uses this to
 * decide which one to load for the device in front of it.
 */
export type AppVariant = 'mobile' | 'desktop'

/** Narrowest viewport at which the editor's table is usable. */
export const DESKTOP_MIN_WIDTH = 1024

/**
 * Phones, whatever the window size. Tablets are deliberately *not* matched:
 * an iPad in landscape is a fine authoring surface, and its UA says
 * "Macintosh" in desktop mode anyway, so width is the better signal there.
 */
const PHONE_UA = /\b(?:iPhone|iPod|Windows Phone|Mobi(?:le)?)\b|\bAndroid\b(?!.*\bTablet\b)/i

export interface VariantSignals {
  userAgent: string
  viewportWidth: number
  /** An explicit choice, if the user made one. Always wins. */
  override?: AppVariant | null
}

export function chooseAppVariant(signals: VariantSignals): AppVariant {
  if (signals.override === 'mobile' || signals.override === 'desktop') return signals.override
  if (PHONE_UA.test(signals.userAgent)) return 'mobile'
  return signals.viewportWidth >= DESKTOP_MIN_WIDTH ? 'desktop' : 'mobile'
}

export function isAppVariant(value: unknown): value is AppVariant {
  return value === 'mobile' || value === 'desktop'
}
